import { useState } from 'react';
import { Download, ExternalLink, Eye } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { get } from '../../nucleo/api';
import { dataHora, dinheiro, nomeDeArquivo, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { CaixaConfig } from './config-crm-base';
import type { Atividade, Oportunidade } from './tipos';

/**
 * Lote crm6, item 17: anexar ao "Criar e-mail" arquivos que já estão na negociação (aba Arquivos), sem baixar e subir de novo.
 * Só os tipos que o e-mail aceita aparecem; o servidor confere de novo (arquivo desta negociação, tipo pelos bytes, teto).
 * Lista de 30/10 (B), item 17: as propostas da negociação também contam como arquivos (em PDF, o mesmo da prévia; `proposta`
 * = o id vai em `propostas` no envio e o servidor gera o arquivo). Item 19: o seletor usa a lista da aba Arquivos (nome, dados
 * e prévia), com a caixa de marcar em cada linha.
 */
export interface ArquivoDaNegociacao {
  id: string;
  nome: string;
  tamanho: number;
  /** Item 17: é uma proposta da negociação (o id é o da proposta; o PDF nasce no envio). */
  proposta?: boolean;
}

/** Os tipos que o e-mail aceita (os mesmos do "Anexar arquivo"). */
export const EXTENSOES_EMAIL = /\.(jpe?g|png|gif|webp|pdf|docx?|xlsx?|pptx?|odt|ods|txt|csv)$/i;
const tamanho = (b: number) => (b >= 1024 * 1024 ? `${numero(b / 1024 / 1024, 1)} MB` : `${numero(Math.max(1, Math.round(b / 1024)), 0)} KB`);

const EXTENSOES: [RegExp, string, string][] = [
  [/\.pdf$/i, 'PDF', 'bg-perigo'],
  [/\.(xlsx?|csv|ods)$/i, 'XLS', 'bg-verde'],
  [/\.(jpe?g|png|gif|webp|svg|heic)$/i, 'IMG', 'bg-roxo'],
  [/\.(zip|rar|7z|gz)$/i, 'ZIP', 'bg-laranja'],
];

/** Ajustes do CRM (27/10, F13): o selo colorido do tipo do arquivo pela extensão (o resto é DOC). */
export function SeloExtensao({ nome }: { nome: string }) {
  const [, rotulo, cor] = EXTENSOES.find(([re]) => re.test(nome)) ?? [null, 'DOC', 'bg-azul'];
  return (
    <span aria-hidden className={`grid h-8 w-10 shrink-0 place-items-center rounded text-xs font-bold text-sup-1 ${cor}`} data-extensao={rotulo}>
      {rotulo}
    </span>
  );
}

/** Uma linha da lista de arquivos da negociação (o arquivo da aba Arquivos ou, no seletor do e-mail, uma proposta). */
export interface LinhaArquivo {
  id: string;
  nome: string;
  mime?: string | null;
  tamanho: number;
  created_at?: string;
  autor_nome?: string | null;
  /** Item 17: a proposta (número e valor) no lugar do tamanho; a prévia é o PDF dela. */
  proposta?: { numero: number; valor: string };
}
export const caminhoDoPdfDaProposta = (id: string) => `/api/v1/pipeline/propostas/${id}/pdf`;

/**
 * Ajustes do CRM (27/10, F13) e lista de 30/10 (B), item 19: a lista da aba Arquivos (selo do tipo, nome, tamanho e "Enviado
 * por ... em", prévia da imagem numa janela e do PDF em outra aba), reaproveitada no seletor do e-mail com `selecao` (a caixa
 * de marcar na frente de cada linha). `baixar`: o nome e o ícone baixam o arquivo; `acoes`: o que vem no fim da linha.
 */
export function ListaArquivosNegociacao({
  arquivos,
  nome,
  baixar = false,
  selecao,
  acoes,
}: {
  arquivos: LinhaArquivo[];
  /** O nome que a tela mostra (modo discreto, com o cliente e as pessoas de contato). */
  nome: (n: string) => string;
  baixar?: boolean;
  selecao?: { marcados: string[]; aoMudar: (ids: string[]) => void };
  acoes?: (a: LinhaArquivo) => React.ReactNode;
}) {
  const { t } = useTranslation('pipeline');
  const [previa, setPrevia] = useState<LinhaArquivo | null>(null);
  return (
    <>
      <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-lista-arquivos="">
        {arquivos.map((a) => {
          const n = nome(a.nome);
          const tipo = a.proposta ? 'pdf' : previaDe(a);
          const marcado = !!selecao?.marcados.includes(a.id);
          return (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-arquivo={a.nome} data-arquivo-proposta={a.proposta ? a.proposta.numero : undefined}>
              {selecao && (
                <CaixaConfig
                  marcado={marcado}
                  rotulo={t('crm.crm6.arquivos.marcar', { nome: n })}
                  aoMudar={(v) => selecao.aoMudar(v ? [...selecao.marcados, a.id] : selecao.marcados.filter((x) => x !== a.id))}
                />
              )}
              <SeloExtensao nome={a.nome} />
              <span className="min-w-0 flex-1">
                {baixar && !a.proposta ? (
                  <a href={`/api/v1/anexos/${a.id}`} className="block truncate font-medium text-acento-forte hover:underline" title={n}>
                    {n}
                  </a>
                ) : (
                  <span className="block truncate font-medium" title={n}>
                    {n}
                  </span>
                )}
                <span className="text-xs text-tinta-3">
                  {a.proposta
                    ? t('crm.crm6.arquivos.dadosProposta', { numero: a.proposta.numero, valor: a.proposta.valor, data: a.created_at ? dataHora(a.created_at) : '' })
                    : `${tamanho(a.tamanho)} · ${a.autor_nome ? t('crm.ficha2.enviadoPor', { nome: nomePessoa(a.autor_nome), data: dataHora(a.created_at ?? '') }) : a.created_at ? dataHora(a.created_at) : ''}`}
                </span>
              </span>
              {tipo === 'imagem' && (
                <button type="button" className="rounded p-1.5 text-tinta-2 hover:bg-sup-3" aria-label={t('crm.crm6.previa.ver', { nome: n })} title={t('crm.crm6.previa.ver', { nome: n })} onClick={() => setPrevia(a)} data-ver-arquivo={a.id}>
                  <Eye className="size-4" aria-hidden />
                </button>
              )}
              {tipo === 'pdf' && (
                <a href={a.proposta ? caminhoDoPdfDaProposta(a.id) : caminhoDaPrevia(a.id)} target="_blank" rel="noopener noreferrer" className="rounded p-1.5 text-tinta-2 hover:bg-sup-3" aria-label={t('crm.crm6.previa.ver', { nome: n })} title={t('crm.crm6.previa.ver', { nome: n })} data-ver-arquivo={a.id}>
                  <Eye className="size-4" aria-hidden />
                </a>
              )}
              {baixar && !a.proposta && (
                <a href={`/api/v1/anexos/${a.id}`} className="rounded p-1.5 text-tinta-2 hover:bg-sup-3" aria-label={t('crm.arquivos.baixar', { nome: n })} title={t('crm.arquivos.baixar', { nome: n })}>
                  <Download className="size-4" aria-hidden />
                </a>
              )}
              {acoes?.(a)}
            </li>
          );
        })}
      </ul>
      {previa && <PreviaImagem arquivo={previa} nome={nome(previa.nome)} aoFechar={() => setPrevia(null)} />}
    </>
  );
}

/**
 * rl0311-5 VIS5-M1: os nomes que o modo discreto troca na negociação: o cliente, as pessoas de contato e as empresas adicionais
 * (a mesma consulta da ficha). Sem a resposta, vale o cliente.
 */
export function useNomesDaNegociacao(oportunidadeId: string, parceiro: string | null | undefined, habilitado = true) {
  const q = useQuery({
    queryKey: ['pipeline', 'crm', 'ficha', oportunidadeId],
    queryFn: () => get<{ contatos: { nome: string }[]; empresas_adicionais?: { nome: string }[] }>(`/pipeline/crm/negociacoes/${oportunidadeId}/ficha`),
    enabled: habilitado,
  });
  return Object.assign([parceiro, ...(q.data?.contatos ?? []).map((c) => c.nome), ...(q.data?.empresas_adicionais ?? []).map((c) => c.nome)], { carregando: habilitado && q.isPending });
}

/** O texto da ata e os arquivos dela, nos dois pop-ups, com os nomes da negociação no modo discreto. */
export function AtaComArquivos({ oportunidadeId, atividade, parceiro }: { oportunidadeId: string; atividade: Pick<Atividade, 'titulo' | 'concluida_em' | 'ata' | 'ata_anexo_ids'>; parceiro: string | null | undefined }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const nomes = useNomesDaNegociacao(oportunidadeId, parceiro);
  // rl0311-6 VIS6-B1: o texto só aparece com os nomes que o modo discreto troca já lidos.
  if (nomes.carregando) return <p role="status" className="text-tinta-3">{tn('comum.carregando')}</p>;
  return (
    <>
      <p className="font-semibold">{textoLivre(atividade.titulo, ...nomes)}</p>
      {atividade.concluida_em && <p className="text-xs text-tinta-3">{t('crm.atividades.concluidaEm', { data: dataHora(atividade.concluida_em) })}</p>}
      <p className="whitespace-pre-wrap">{textoLivre(atividade.ata, ...nomes)}</p>
      <ArquivosDaAta oportunidadeId={oportunidadeId} ids={atividade.ata_anexo_ids ?? []} nomes={nomes} />
    </>
  );
}

/**
 * Lista de 03/11, item 5: os arquivos anexados à ata, nos pop-ups da tarefa concluída (Atividades e ficha), com o nome, a prévia
 * e o baixar da lista da aba Arquivos. Os dados vêm dos arquivos da negociação; o que saiu dela só conta no aviso.
 */
export function ArquivosDaAta({ oportunidadeId, ids, nomes }: { oportunidadeId: string; ids: readonly string[]; nomes: (string | null | undefined)[] }) {
  const { t } = useTranslation('pipeline');
  const q = useQuery({
    queryKey: ['chatter', 'pipeline.oportunidade', oportunidadeId],
    queryFn: () => get<{ anexos: (Omit<LinhaArquivo, 'proposta'> & { mime?: string })[] }>(`/chatter/pipeline.oportunidade/${oportunidadeId}`),
    enabled: ids.length > 0,
  });
  if (!ids.length) return null;
  const arquivos = (q.data?.anexos ?? []).filter((a) => ids.includes(a.id));
  const fora = ids.length - arquivos.length;
  return (
    <section className="mt-2 flex min-w-0 flex-col gap-2" data-ata-arquivos="">
      <h3 className="font-semibold">{t('crm.atividades.ataArquivos')}</h3>
      <Estado consulta={q}>
        {() => (
          <>
            {arquivos.length > 0 && <ListaArquivosNegociacao arquivos={arquivos} nome={(n) => nomeDeArquivo(n, ...nomes)} baixar />}
            {fora > 0 && (
              <p className="text-xs text-tinta-3" data-ata-arquivos-fora="">
                {t('crm.atividades.ataArquivosFora', { count: fora })}
              </p>
            )}
          </>
        )}
      </Estado>
    </section>
  );
}

interface PropostaDaLista {
  id: string;
  numero: number;
  total_centavos: number;
  moeda: string;
  created_at: string;
}

/** `nomes`: rodada rlote-8 (VIS-M1), o cliente e as pessoas de contato que o modo discreto troca no nome do arquivo. */
export function EscolherArquivosDaNegociacao({
  op,
  ja,
  aoFechar,
  aoEscolher,
  nomes,
  semProposta,
}: {
  op: Oportunidade;
  ja: string[];
  aoFechar: () => void;
  aoEscolher: (arquivos: ArquivoDaNegociacao[]) => void;
  nomes?: (string | null | undefined)[];
  /** No envio da proposta, ela mesma já vai em anexo: não aparece na lista. */
  semProposta?: string;
}) {
  const { t } = useTranslation('pipeline');
  const q = useQuery({ queryKey: ['chatter', 'pipeline.oportunidade', op.id], queryFn: () => get<{ anexos: (Omit<LinhaArquivo, 'proposta'> & { mime?: string })[] }>(`/chatter/pipeline.oportunidade/${op.id}`) });
  // Item 17: as propostas da negociação (a mesma consulta da aba Propostas).
  const propostas = useQuery({ queryKey: ['pipeline', 'propostas', op.id], queryFn: () => get<{ itens: PropostaDaLista[] }>(`/pipeline/oportunidades/${op.id}/propostas`) });
  const aceitos = (q.data?.anexos ?? []).filter((a) => EXTENSOES_EMAIL.test(a.nome));
  const fora = (q.data?.anexos ?? []).length - aceitos.length;
  // A proposta já enviada tem o PDF nos arquivos (mesmo nome): ela não se repete.
  const nomesJa = new Set(aceitos.map((a) => a.nome));
  const dasPropostas: LinhaArquivo[] = (propostas.data?.itens ?? [])
    .map((p) => ({ id: p.id, nome: nomeDoPdf(p.numero, op.titulo), tamanho: 0, created_at: p.created_at, proposta: { numero: p.numero, valor: dinheiro(p.total_centavos, p.moeda) } }))
    .filter((p) => p.id !== semProposta && !nomesJa.has(p.nome));
  const linhas: LinhaArquivo[] = [...dasPropostas, ...aceitos];
  const [marcados, setMarcados] = useState<string[]>(ja);
  const arq = (n: string) => nomeDeArquivo(n, ...(nomes ?? [op.parceiro_nome]));
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('crm.crm6.arquivos.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('crm.crm6.arquivos.cancelar')}
          </Botao>
          <Botao
            variante="primario"
            disabled={!q.isSuccess}
            onClick={() =>
              aoEscolher([
                ...dasPropostas.filter((a) => marcados.includes(a.id)).map((a) => ({ id: a.id, nome: a.nome, tamanho: 0, proposta: true })),
                ...aceitos.filter((a) => marcados.includes(a.id)).map((a) => ({ id: a.id, nome: a.nome, tamanho: a.tamanho })),
              ])
            }
            data-anexar-da-negociacao-confirmar=""
          >
            {t('crm.crm6.arquivos.anexar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-arquivos-da-negociacao="">
        {q.isPending && <p className="text-sm text-tinta-3">{t('crm.crm6.arquivos.carregando')}</p>}
        {q.isSuccess && !linhas.length && <p className="text-sm text-tinta-3">{t('crm.crm6.arquivos.vazio')}</p>}
        {linhas.length > 0 && (
          <div role="group" aria-label={t('crm.crm6.arquivos.rotulo')} className="max-h-96 overflow-y-auto">
            <ListaArquivosNegociacao arquivos={linhas} nome={arq} selecao={{ marcados, aoMudar: setMarcados }} />
          </div>
        )}
        {fora > 0 && <p className="text-xs text-tinta-3">{t('crm.crm6.arquivos.foraDoTipo', { count: fora })}</p>}
      </div>
    </Modal>
  );
}

/** O nome do PDF da proposta, igual ao do servidor (`nomeDoPdfDaProposta` do Pipeline). */
export function nomeDoPdf(numero: number, negociacao: string): string {
  const base = negociacao.normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 80);
  return `Proposta-${numero}${base ? `-${base}` : ''}.pdf`;
}

/** Lote crm6, item 6: os tipos com prévia na tela (o servidor confere de novo pelos bytes; SVG e HTML nunca). */
const PREVIA_IMAGEM = /\.(jpe?g|png|gif|webp)$/i;
const MIME_IMAGEM = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
export type Previa = 'imagem' | 'pdf' | null;
/** Função pura: que prévia o arquivo tem pelo nome e pelo tipo gravado (os dois precisam bater). */
export function previaDe(a: { nome: string; mime?: string | null }): Previa {
  const mime = String(a.mime ?? '').split(';')[0]!.trim().toLowerCase();
  if (PREVIA_IMAGEM.test(a.nome) && MIME_IMAGEM.has(mime)) return 'imagem';
  if (/\.pdf$/i.test(a.nome) && mime === 'application/pdf') return 'pdf';
  return null;
}
export const caminhoDaPrevia = (id: string) => `/api/v1/anexos/${id}/previa`;

/**
 * A janela da prévia da imagem. A imagem vem da rota de prévia (inline, `nosniff`, CSP com `sandbox`); se o servidor recusar
 * (o tipo dos bytes não é imagem), fica o aviso e o botão de baixar. O PDF abre em outra aba pela mesma rota: o sistema não
 * deixa nenhuma página ser emoldurada (`frame-src` e `X-Frame-Options`), então o PDF não vai num quadro dentro da tela.
 */
export function PreviaImagem({ arquivo, nome, aoFechar }: { arquivo: { id: string }; nome: string; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const [falhou, setFalhou] = useState(false);
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={nome}
      rodape={
        <>
          <a href={caminhoDaPrevia(arquivo.id)} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-acento-forte hover:underline md:min-h-9">
            <ExternalLink className="size-4" aria-hidden />
            {t('crm.crm6.previa.novaAba')}
          </a>
          <a href={`/api/v1/anexos/${arquivo.id}`} className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-acento-forte hover:underline md:min-h-9" data-previa-baixar="">
            <Download className="size-4" aria-hidden />
            {t('crm.crm6.previa.baixar')}
          </a>
        </>
      }
    >
      <div className="flex justify-center" data-previa-arquivo={arquivo.id}>
        {falhou ? (
          <p className="text-sm text-tinta-3" data-previa-falhou="">
            {t('crm.crm6.previa.falhou')}
          </p>
        ) : (
          <img src={caminhoDaPrevia(arquivo.id)} alt={nome} className="previa-imagem max-w-full rounded-lg object-contain" onError={() => setFalhou(true)} />
        )}
      </div>
    </Modal>
  );
}
